// Bondsregister in de ledenadministratie: graduaties, licenties en schorsingen
// (stap K, lichte versie). Alleen zichtbaar met de module bond.
//
// Elke wijziging is een voorstel aan de dispatcher. Bij een bond staat vierogen
// altijd aan, dus er verandert pas iets als een tweede bevoegde goedkeurt. Wat
// mag en wat klopt, beslist de database; dit scherm legt het voor en toont het.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft, heeftModule } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { dispatchFout, vernieuwTeller } from './goedkeuringen.js';

const sb = () => getClient();
const vandaag = () => new Date().toISOString().slice(0, 10);

function datum(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
export function schaalFout(e) {
  const m = String(e?.message || e || '').match(/schaal:([a-z_]+)/);
  return m ? t('schaal.fout.' + m[1]) : t('alg.fout', { melding: e?.message || String(e) });
}
const nummerTekst = (label, nummer) => t('bond.nummer', { label: label || t('bond.nummer.standaard'), nummer });
const klein = tekst => `<p style="font-size:12px;color:var(--text-3)">${esc(tekst)}</p>`;
const knop = (actie, icoon, tekst, soort = 'btn-default', extra = '') =>
  `<button type="button" class="btn ${soort} btn-sm" data-bond="${actie}" ${extra}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;

export const actief = () => heeftModule('bond');

// ── Voor de ledenlijst ─────────────────────────────────────────────────────
export async function laadStatus(orgId) {
  if (!actief()) return {};
  const { data, error } = await sb().from('bondsstatus').select('person_id, graad, geschorst, geschorst_tot, licenties')
    .eq('organisation_id', orgId);
  if (error) return {};
  return Object.fromEntries((data || []).map(r => [r.person_id, r]));
}

export function schorsingBadge(s) {
  if (!s?.geschorst) return '';
  const tekst = s.geschorst_tot ? t('bond.geschorst.tot', { datum: datum(s.geschorst_tot) }) : t('bond.geschorst');
  return ` <span class="badge badge-red"><i class="ti ti-ban" style="font-size:10px"></i> ${esc(tekst)}</span>`;
}

// ── Voorstellen ────────────────────────────────────────────────────────────
async function stelVoor(handeling, gegevens, reden, naDeze) {
  if (!reden || reden.trim().length < 3) { toast(t('dispatch.fout.reden_ontbreekt'), 'error'); return; }
  const { data, error } = await sb().rpc('dispatch', { p_handeling: handeling, p_gegevens: gegevens, p_reden: reden.trim() });
  if (error) { toast(dispatchFout(error), 'error'); return; }
  toast(t(data?.status === 'wacht' ? 'vierogen.ingediend' : 'vierogen.uitgevoerd'), 'success');
  await vernieuwTeller();
  await naDeze();
}

// ── Sectie in het ledendetail ──────────────────────────────────────────────
export async function renderSectie(el, p) {
  if (!el || !p?.id || !actief()) return;
  el.innerHTML = '<div class="spinner"></div>';
  const [gr, li, sc, schaal, soorten, redenen, open, lm] = await Promise.all([
    sb().from('graduaties').select('*').eq('person_id', p.id),
    sb().from('licenties').select('*').eq('person_id', p.id),
    sb().from('schorsingen').select('*').eq('person_id', p.id),
    sb().from('graduatieschaal').select('code, naam, volgorde, nummer_label').eq('organisation_id', p.organisation_id),
    sb().from('licence_types').select('*').eq('organisation_id', p.organisation_id),
    heeft('member.suspend') ? sb().from('suspension_reasons').select('relation_id, reden').eq('organisation_id', p.organisation_id)
      : Promise.resolve({ data: [] }),
    sb().from('approval_overzicht').select('id, action_type, payload, submitted_by, object_detail, status')
      .eq('organisation_id', p.organisation_id).eq('status', 'open'),
    sb().from('person_relations').select('id, status, ends_on, starts_on, club_register_id, vrijstelling, club_register:club_register_id(naam)')
      .eq('person_id', p.id).eq('kind', 'lidmaatschap'),
  ]);
  if (gr.error || schaal.error) { el.innerHTML = klein(t('bond.nietbeschikbaar')); return; }

  const graden = [...(gr.data || [])].sort((a, b) => (b.volgorde ?? -1) - (a.volgorde ?? -1) || String(b.datum).localeCompare(String(a.datum)));
  const lic = [...(li.data || [])].sort((a, b) => String(b.starts_on).localeCompare(String(a.starts_on)));
  const schors = [...(sc.data || [])].sort((a, b) => String(b.starts_on).localeCompare(String(a.starts_on)));
  const lidRel = (lm.data || []).filter(r => r.status === 'actief' && (!r.ends_on || r.ends_on >= vandaag()))
    .sort((a, b) => String(b.starts_on).localeCompare(String(a.starts_on)))[0] || null;
  const relIds = new Set([...graden, ...lic, ...schors, ...(lidRel ? [lidRel] : [])].map(r => r.id));
  const wacht = (open.data || []).filter(v => v.payload?.person_id === p.id || relIds.has(v.payload?.relation_id));
  const reden = Object.fromEntries((redenen.data || []).map(r => [r.relation_id, r.reden]));
  const lopend = schors.find(s => s.loopt || s.komend);
  const schaalOpVolgorde = [...(schaal.data || [])].sort((a, b) => a.volgorde - b.volgorde);
  const actieveSoorten = (soorten.data || []).filter(s => s.actief);

  const redenVeld = naam => `<div class="form-group" style="flex:2;min-width:200px"><label class="form-label">${esc(t('bond.reden'))}</label>
      <input class="form-input" name="${naam}" placeholder="${esc(t('bond.reden.voorbeeld'))}"></div>`;

  el.innerHTML = `
    <div class="section-title" style="margin-top:8px"><i class="ti ti-award"></i> ${esc(t('bond.sectie'))}</div>
    <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('bond.uitleg'))}</div>
    ${wacht.map(v => `<div class="banner banner-amber"><i class="ti ti-hourglass"></i>
      <span>${esc(t('vierogen.wacht', { handeling: t('vierogen.handeling.' + v.action_type) + (v.object_detail ? ': ' + v.object_detail : ''), door: v.submitted_by }))}</span></div>`).join('')}

    <div style="font-size:13px;font-weight:600;margin:10px 0 6px">${esc(t('bond.graduaties'))}</div>
    ${graden.map(g => `<div class="org-row">
        <div style="flex:1;font-size:13px">${esc(g.naam || '')}
          <span style="font-size:11px;color:var(--text-3)">${esc(datum(g.datum))}${g.nummer ? ' &middot; ' + esc(nummerTekst(null, g.nummer)) : ''}${g.examinator ? ' &middot; ' + esc(g.examinator) : ''}${g.bron ? ' &middot; ' + esc(t('bond.bron', { bron: g.bron })) : ''}</span></div>
        ${heeft('grade.set') ? knop('intrek', 'arrow-back-up', t('bond.intrekken'), 'btn-ghost', `data-id="${g.id}" data-handeling="grade.revoke"`) : ''}
      </div>`).join('') || klein(t('bond.graduaties.geen'))}
    ${heeft('grade.set') ? `<div class="form-row" style="margin-top:6px;align-items:flex-end">
      <div class="form-group"><label class="form-label">${esc(t('bond.graad'))}</label>
        <select class="form-input" name="bond_graad">${schaalOpVolgorde.map(s => `<option value="${esc(s.code)}" data-label="${esc(s.nummer_label || '')}">${esc(s.naam)}</option>`).join('')}</select></div>
      <div class="form-group" data-groep="graad-nummer" hidden><label class="form-label" data-nummer-label></label>
        <input class="form-input" name="bond_graad_nummer"></div>
      <div class="form-group"><label class="form-label">${esc(t('bond.datum'))}</label>
        <input class="form-input" type="date" name="bond_graad_datum" value="${vandaag()}" max="${vandaag()}"></div>
      <div class="form-group"><label class="form-label">${esc(t('bond.examinator'))}</label>
        <input class="form-input" name="bond_examinator"></div>
      ${redenVeld('bond_graad_reden')}
      ${knop('graad', 'send', t('bond.voorstellen'), 'btn-primary')}
    </div>` : ''}

    <div style="font-size:13px;font-weight:600;margin:14px 0 6px">${esc(t('bond.licenties'))}</div>
    ${lic.map(l => `<div class="org-row">
        <div style="flex:1;font-size:13px">${esc(l.naam || '')}
          <span style="font-size:11px;color:var(--text-3)">${esc(datum(l.starts_on))}${l.ends_on ? ' &ndash; ' + esc(datum(l.ends_on)) : ''}${l.nummer ? ' &middot; ' + esc(nummerTekst(l.nummer_label, l.nummer)) : ''}</span></div>
        <span class="badge ${l.geldig ? 'badge-green' : l.ingetrokken ? 'badge-red' : 'badge-grey'}">${esc(t(l.geldig ? 'bond.licentie.geldig' : l.ingetrokken ? 'bond.licentie.ingetrokken' : 'bond.licentie.verlopen'))}</span>
        ${heeft('licence.grant') && l.geldig ? knop('intrek', 'x', t('bond.intrekken'), 'btn-ghost', `data-id="${l.id}" data-handeling="licence.revoke"`) : ''}
      </div>`).join('') || klein(t('bond.licenties.geen'))}
    ${heeft('licence.grant') ? (actieveSoorten.length ? `<div class="form-row" style="margin-top:6px;align-items:flex-end">
      <div class="form-group"><label class="form-label">${esc(t('bond.licentie.soort'))}</label>
        <select class="form-input" name="bond_licentie">${actieveSoorten.map(s => `<option value="${s.id}" data-label="${esc(s.nummer_label || '')}" data-verplicht="${s.nummer_verplicht ? '1' : ''}">${esc(s.naam)}${s.geldigheid_maanden ? ' (' + esc(t('bond.maanden', { aantal: s.geldigheid_maanden })) + ')' : ''}</option>`).join('')}</select></div>
      <div class="form-group" data-groep="licentie-nummer" hidden><label class="form-label" data-nummer-label></label>
        <input class="form-input" name="bond_licentie_nummer"></div>
      <div class="form-group"><label class="form-label">${esc(t('bond.vanaf'))}</label>
        <input class="form-input" type="date" name="bond_licentie_vanaf" value="${vandaag()}"></div>
      <div class="form-group"><label class="form-label">${esc(t('bond.tot.optioneel'))}</label>
        <input class="form-input" type="date" name="bond_licentie_tot"></div>
      ${redenVeld('bond_licentie_reden')}
      ${knop('licentie', 'send', t('bond.voorstellen'), 'btn-primary')}
    </div>` : klein(t('bond.licentie.geensoorten'))) : ''}

    <div style="font-size:13px;font-weight:600;margin:14px 0 6px">${esc(t('bond.vrijstelling'))}</div>
    ${!lidRel ? klein(t('bond.vrijstelling.geenlidmaatschap'))
      : lidRel.club_register_id ? klein(t('bond.vrijstelling.club', { club: lidRel.club_register?.naam || '' }) + '. ' + t('bond.vrijstelling.geen'))
      : lidRel.vrijstelling ? `<div class="org-row"><div style="flex:1;font-size:13px">${esc(t('bond.vrijstelling.nu', { soort: t('bond.vrijstelling.' + lidRel.vrijstelling) }))}</div></div>
        ${heeft('club.manage') ? `<div class="form-row" style="align-items:flex-end">${redenVeld('bond_vrij_reden')}
          ${knop('onvrij', 'lock-open', t('bond.vrijstelling.opheffen'), 'btn-default', `data-id="${lidRel.id}"`)}</div>` : ''}`
      : `${klein(t('bond.vrijstelling.uitleg'))}
        ${heeft('club.manage') ? `<div class="form-row" style="margin-top:6px;align-items:flex-end">
          <div class="form-group"><label class="form-label">${esc(t('bond.vrijstelling.soort'))}</label>
            <select class="form-input" name="bond_vrij_soort">${['grand_master', 'maatwerk'].map(x => `<option value="${x}">${esc(t('bond.vrijstelling.' + x))}</option>`).join('')}</select></div>
          ${redenVeld('bond_vrij_reden')}
          ${knop('vrij', 'send', t('bond.vrijstelling.vrijstellen'), 'btn-primary', `data-id="${lidRel.id}"`)}</div>` : ''}`}

    <div style="font-size:13px;font-weight:600;margin:14px 0 6px">${esc(t('bond.schorsing'))}</div>
    ${lopend ? `<div class="banner banner-red"><i class="ti ti-ban"></i><span>
        ${esc(lopend.ends_on ? t('bond.schorsing.periode', { van: datum(lopend.starts_on), tot: datum(lopend.ends_on) }) : t('bond.schorsing.onbepaald', { van: datum(lopend.starts_on) }))}
        ${reden[lopend.id] ? '<br>' + esc(t('bond.schorsing.reden', { reden: reden[lopend.id] })) : ''}</span></div>
      ${heeft('member.suspend') ? `<div class="form-row" style="align-items:flex-end">${redenVeld('bond_opheffen_reden')}
        ${knop('opheffen', 'lock-open', t('bond.schorsing.opheffen'), 'btn-default', `data-id="${lopend.id}"`)}</div>` : ''}`
      : klein(t('bond.schorsing.geen'))}
    ${heeft('member.suspend') && !lopend ? `<div class="form-row" style="margin-top:6px;align-items:flex-end">
      <div class="form-group"><label class="form-label">${esc(t('bond.vanaf'))}</label>
        <input class="form-input" type="date" name="bond_schors_vanaf" value="${vandaag()}"></div>
      <div class="form-group"><label class="form-label">${esc(t('bond.tot.onbepaald'))}</label>
        <input class="form-input" type="date" name="bond_schors_tot"></div>
      ${redenVeld('bond_schors_reden')}
      ${knop('schorsen', 'ban', t('bond.schorsen'), 'btn-danger')}
    </div>
    <div style="font-size:11px;color:var(--text-3)">${esc(t('bond.schorsing.zichtbaar'))}</div>` : ''}`;

  const waarde = naam => el.querySelector(`[name="${naam}"]`)?.value || '';
  // Een nummer gaat alleen mee als het veld in beeld is (de graad of soort kent een nummer).
  const nummerWaarde = (groep, naam) => (el.querySelector(`[data-groep="${groep}"]`)?.hidden ? null : waarde(naam).trim() || null);
  const opnieuw = () => renderSectie(el, p);
  // Toon het nummerveld met het label van de gekozen graad of licentiesoort.
  const zetNummerVelden = () => {
    for (const [keuze, groep] of [['bond_graad', 'graad-nummer'], ['bond_licentie', 'licentie-nummer']]) {
      const sel = el.querySelector(`[name="${keuze}"]`);
      const g = el.querySelector(`[data-groep="${groep}"]`);
      if (!sel || !g) continue;
      const opt = sel.selectedOptions[0];
      const label = opt?.dataset.label || '';
      g.hidden = !label;
      g.querySelector('[data-nummer-label]').textContent = label + (opt?.dataset.verplicht ? ' *' : '');
    }
  };
  zetNummerVelden();
  el.onchange = e => { if (e.target.matches('[name="bond_graad"], [name="bond_licentie"]')) zetNummerVelden(); };
  el.onclick = async e => {
    const k = e.target.closest('[data-bond]');
    if (!k) return;
    const a = k.dataset.bond;
    if (a === 'graad') await stelVoor('grade.set', { person_id: p.id, code: waarde('bond_graad'), datum: waarde('bond_graad_datum'),
      examinator: waarde('bond_examinator') || null, nummer: nummerWaarde('graad-nummer', 'bond_graad_nummer') }, waarde('bond_graad_reden'), opnieuw);
    // Intrekken: eerst een reden, in de regel zelf.
    if (a === 'intrek') {
      const rij = k.closest('.org-row');
      if (rij.nextElementSibling?.dataset.intrekVoor === k.dataset.id) { rij.nextElementSibling.remove(); return; }
      rij.insertAdjacentHTML('afterend', `<div class="form-row" data-intrek-voor="${esc(k.dataset.id)}" style="align-items:flex-end">
        ${redenVeld('bond_intrek_reden')}
        ${knop('intrek-ok', 'send', t('bond.voorstellen'), 'btn-primary', `data-id="${esc(k.dataset.id)}" data-handeling="${esc(k.dataset.handeling)}"`)}</div>`);
      return;
    }
    if (a === 'intrek-ok') await stelVoor(k.dataset.handeling, { relation_id: k.dataset.id }, waarde('bond_intrek_reden'), opnieuw);
    if (a === 'licentie') await stelVoor('licence.grant', { person_id: p.id, soort_id: waarde('bond_licentie'),
      datum: waarde('bond_licentie_vanaf'), tot: waarde('bond_licentie_tot') || null, nummer: nummerWaarde('licentie-nummer', 'bond_licentie_nummer') }, waarde('bond_licentie_reden'), opnieuw);
    if (a === 'schorsen') await stelVoor('member.suspend', { person_id: p.id, datum: waarde('bond_schors_vanaf'),
      tot: waarde('bond_schors_tot') || null }, waarde('bond_schors_reden'), opnieuw);
    if (a === 'vrij') await stelVoor('member.exempt', { relation_id: k.dataset.id, soort: waarde('bond_vrij_soort') }, waarde('bond_vrij_reden'), opnieuw);
    if (a === 'onvrij') await stelVoor('member.unexempt', { relation_id: k.dataset.id }, waarde('bond_vrij_reden'), opnieuw);
    if (a === 'opheffen') await stelVoor('member.unsuspend', { relation_id: k.dataset.id }, waarde('bond_opheffen_reden'), opnieuw);
  };
}

// ── Instellingen: graduatieschaal en licentiesoorten ───────────────────────
function modal() {
  let m = document.getElementById('bondModal');
  if (!m) {
    m = document.createElement('div');
    m.className = 'modal-overlay';
    m.id = 'bondModal';
    m.innerHTML = '<div class="modal" style="max-width:680px"><div id="bondModalInhoud" style="display:flex;flex-direction:column;min-height:0;flex:1"></div></div>';
    document.body.appendChild(m);
    m.addEventListener('click', e => {
      if (e.target === m || e.target.closest('[data-bond="sluit"]')) m.classList.remove('open');
    });
  }
  return m;
}

export async function openInstellingen() {
  const m = modal();
  const el = m.querySelector('#bondModalInhoud');
  const org = state.currentOrgId;
  const [schaal, soorten, catalogus, gekozen] = await Promise.all([
    sb().from('graduatieschaal').select('*').eq('organisation_id', org),
    sb().from('licence_types').select('*').eq('organisation_id', org),
    sb().from('grade_scales').select('id, code, naam').eq('actief', true),
    sb().from('organisation_grade_scale').select('scale_id').eq('organisation_id', org).maybeSingle(),
  ]);
  const schalen = [...(catalogus.data || [])].sort((a, b) => a.naam.localeCompare(b.naam, locale()));
  const huidigeSchaal = gekozen.data?.scale_id || schalen.find(x => x.code === 'itf')?.id;
  const rijen = [...(schaal.data || [])].sort((a, b) => a.volgorde - b.volgorde);
  const standaard = rijen.length && rijen[0].standaard;
  const lt = [...(soorten.data || [])].sort((a, b) => a.naam.localeCompare(b.naam, locale()));

  el.innerHTML = `
    <div class="modal-header"><span class="modal-title">${esc(t('bond.instellingen'))}</span>
      <button class="btn-icon" data-bond="sluit" title="${esc(t('alg.sluiten'))}"><i class="ti ti-x"></i></button></div>
    <div class="modal-body">
      <div class="section-title"><i class="ti ti-stairs-up"></i> ${esc(t('bond.schaal'))}</div>
      <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t(standaard ? 'bond.schaal.standaard' : 'bond.schaal.eigen'))}</div>
      ${standaard ? `<div class="form-row" style="align-items:flex-end;margin-bottom:8px">
          <div class="form-group"><label class="form-label">${esc(t('bond.schaal.kies'))}</label>
            <select class="form-input" name="schaal_kies">${schalen.map(x => `<option value="${esc(x.id)}" ${x.id === huidigeSchaal ? 'selected' : ''}>${esc(x.naam)}</option>`).join('')}</select></div>
          ${knop('schaal-kiezen', 'check', t('bond.schaal.kies.knop'), 'btn-primary')}
        </div>` + knop('schaal-eigen', 'pencil', t('bond.schaal.aanpassen')) + `<div style="font-size:12px;margin-top:8px">${rijen.map(r => esc(r.naam)).join(' &middot; ')}</div>` : `
      <div id="bondSchaal">${rijen.map((r, i) => `<div class="org-row" data-code="${esc(r.code)}">
          <span style="font-family:var(--mono);font-size:11px;color:var(--text-3);width:28px">${i + 1}</span>
          <input class="form-input" data-veld="naam" value="${esc(r.naam)}" style="flex:1">
          ${knop('omhoog', 'arrow-up', '', 'btn-ghost', `data-code="${esc(r.code)}" ${i === 0 ? 'disabled' : ''} title="${esc(t('bond.omhoog'))}"`)}
          ${knop('graad-verwijder', 'trash', '', 'btn-ghost', `data-code="${esc(r.code)}" title="${esc(t('alg.verwijderen'))}"`)}
        </div>`).join('')}</div>
      <div class="form-row" style="align-items:flex-end;margin-top:6px">
        <div class="form-group"><label class="form-label">${esc(t('bond.schaal.nieuw'))}</label><input class="form-input" name="nieuwe_graad"></div>
        ${knop('graad-toevoegen', 'plus', t('bond.toevoegen'))}
        ${knop('schaal-opslaan', 'device-floppy', t('alg.opslaan'), 'btn-primary')}
      </div>`}

      <div class="section-title" style="margin-top:14px"><i class="ti ti-certificate"></i> ${esc(t('bond.soorten'))}</div>
      <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('bond.soorten.uitleg'))}</div>
      ${lt.map(s => `<div class="org-row">
          <div style="flex:1;font-size:13px">${esc(s.naam)} <span style="font-size:11px;color:var(--text-3)">${esc(s.geldigheid_maanden ? t('bond.maanden', { aantal: s.geldigheid_maanden }) : t('bond.onbeperkt'))}${s.nummer_label ? ' &middot; ' + esc(t('bond.soort.metnummer', { label: s.nummer_label })) + (s.nummer_verplicht ? ' (' + esc(t('bond.soort.verplicht')) + ')' : '') : ''}</span></div>
          <label style="display:flex;align-items:center;gap:6px;font-size:12px">
            <input type="checkbox" data-soort="${s.id}" ${s.actief ? 'checked' : ''}> ${esc(t('bond.soort.actief'))}</label>
        </div>`).join('') || klein(t('bond.soorten.geen'))}
      <div class="form-row" style="align-items:flex-end;margin-top:6px">
        <div class="form-group" style="flex:2"><label class="form-label">${esc(t('bond.soort.naam'))}</label><input class="form-input" name="soort_naam"></div>
        <div class="form-group"><label class="form-label">${esc(t('bond.soort.geldigheid'))}</label><input class="form-input" type="number" min="1" max="120" name="soort_maanden"></div>
        <div class="form-group"><label class="form-label">${esc(t('bond.soort.nummerlabel'))}</label><input class="form-input" name="soort_label" placeholder="${esc(t('bond.soort.nummerlabel.voorbeeld'))}"></div>
        <label style="display:flex;align-items:center;gap:6px;font-size:12px;padding-bottom:8px"><input type="checkbox" name="soort_verplicht"> ${esc(t('bond.soort.verplicht'))}</label>
        ${knop('soort-toevoegen', 'plus', t('bond.toevoegen'), 'btn-primary')}
      </div>
    </div>
    <div class="modal-footer"><button class="btn btn-default" data-bond="sluit">${esc(t('alg.sluiten'))}</button></div>`;
  m.classList.add('open');

  const fout = error => { toast(dispatchFout(error), 'error'); };
  const lees = () => [...el.querySelectorAll('#bondSchaal > .org-row')].map(r => ({ code: r.dataset.code, naam: r.querySelector('[data-veld="naam"]').value.trim() }));
  const codeVan = naam => (naam.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'graad').slice(0, 30);

  el.onclick = async e => {
    const k = e.target.closest('[data-bond]');
    if (!k) return;
    const a = k.dataset.bond;
    if (a === 'schaal-kiezen') {
      const { error } = await sb().rpc('graduatieschaal_kiezen', { p_org: org, p_scale: el.querySelector('[name="schaal_kies"]').value });
      if (error) { toast(schaalFout(error), 'error'); return; }
      toast(t('leden.opgeslagen'), 'success');
      return openInstellingen();
    }
    if (a === 'schaal-eigen') {
      const { error } = await sb().rpc('graduatieschaal_eigen', { p_org: org });
      if (error) return fout(error);
      return openInstellingen();
    }
    if (a === 'omhoog' || a === 'graad-verwijder') {
      const rij = [...el.querySelectorAll('#bondSchaal > .org-row')].find(r => r.dataset.code === k.dataset.code);
      if (a === 'omhoog' && rij.previousElementSibling) rij.parentNode.insertBefore(rij, rij.previousElementSibling);
      if (a === 'graad-verwijder') rij.remove();
      return;
    }
    if (a === 'graad-toevoegen') {
      const naam = el.querySelector('[name="nieuwe_graad"]').value.trim();
      if (!naam) return;
      const bestaand = new Set(lees().map(r => r.code));
      let code = codeVan(naam); let i = 2;
      while (bestaand.has(code)) code = codeVan(naam) + '_' + i++;
      el.querySelector('#bondSchaal').insertAdjacentHTML('beforeend', `<div class="org-row" data-code="${esc(code)}">
        <span style="width:28px"></span><input class="form-input" data-veld="naam" value="${esc(naam)}" style="flex:1">
        ${knop('omhoog', 'arrow-up', '', 'btn-ghost', `data-code="${esc(code)}"`)}${knop('graad-verwijder', 'trash', '', 'btn-ghost', `data-code="${esc(code)}"`)}</div>`);
      el.querySelector('[name="nieuwe_graad"]').value = '';
      return;
    }
    if (a === 'schaal-opslaan') {
      const nieuw = lees();
      if (!nieuw.length || nieuw.some(r => !r.naam)) { toast(t('bond.schaal.leeg'), 'error'); return; }
      const weg = rijen.filter(r => !nieuw.some(n => n.code === r.code)).map(r => r.code);
      if (weg.length) {
        const { error } = await sb().from('grade_levels').delete().eq('organisation_id', org).in('code', weg);
        if (error) return fout(error);
      }
      const { error } = await sb().from('grade_levels').upsert(nieuw.map((r, i) => ({ organisation_id: org, code: r.code, naam: r.naam, volgorde: i + 1 })),
        { onConflict: 'organisation_id,code' });
      if (error) return fout(error);
      toast(t('leden.opgeslagen'), 'success');
      return openInstellingen();
    }
    if (a === 'soort-toevoegen') {
      const naam = el.querySelector('[name="soort_naam"]').value.trim();
      const maanden = parseInt(el.querySelector('[name="soort_maanden"]').value, 10);
      if (!naam) return;
      const label = el.querySelector('[name="soort_label"]').value.trim() || null;
      const verplicht = !!label && el.querySelector('[name="soort_verplicht"]').checked;
      const { error } = await sb().from('licence_types').insert([{ organisation_id: org, naam, geldigheid_maanden: Number.isFinite(maanden) ? maanden : null,
        nummer_label: label, nummer_verplicht: verplicht }]);
      if (error) return fout(error);
      return openInstellingen();
    }
  };
  el.onchange = async e => {
    const cb = e.target.closest('[data-soort]');
    if (!cb) return;
    const { error } = await sb().from('licence_types').update({ actief: cb.checked }).eq('id', cb.dataset.soort);
    if (error) { fout(error); cb.checked = !cb.checked; }
  };
}
